import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { deleteLmsLink, getAllLmsLinks, saveLmsLink } from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import { APPRAISAL_LIMITS, clampLen, validateFields } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import "./Appraisal.css";

const emptyForm = () => ({
  id: "",
  name: "",
  description: "",
  buttonText: "Open course",
  buttonLink: "",
  isNewTab: true,
  sortOrder: 0,
  isActive: true,
});

const normalizeLink = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id ?? raw.Id,
    name: raw.name ?? raw.Name ?? "",
    description: raw.description ?? raw.Description ?? "",
    buttonText: raw.buttonText ?? raw.ButtonText ?? "Open course",
    buttonLink: raw.buttonLink ?? raw.ButtonLink ?? "",
    isNewTab: raw.isNewTab ?? raw.IsNewTab,
    sortOrder: raw.sortOrder ?? raw.SortOrder ?? 0,
    isActive: raw.isActive ?? raw.IsActive,
  };
};

const AppraisalLmsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [helpKey, setHelpKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isEditing = !!form.id;

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const list = await getAllLmsLinks();
      setRows(Array.isArray(list) ? list.map(normalizeLink).filter(Boolean) : []);
    } catch (e) {
      setError(e?.message || "Failed to load LMS courses");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const err = validateFields([
      { value: form.name, required: true, max: APPRAISAL_LIMITS.name, label: "Course name" },
      { value: form.description, max: APPRAISAL_LIMITS.longText, label: "Description" },
      { value: form.buttonText, required: true, max: APPRAISAL_LIMITS.buttonText, label: "Button label" },
      { value: form.buttonLink, required: true, max: APPRAISAL_LIMITS.buttonLink, label: "Course URL" },
    ]);
    if (err) {
      setError(err);
      return;
    }
    if (form.buttonLink && !/^https?:\/\//i.test(form.buttonLink.trim())) {
      setError(
        t("app.appraisal.lms.urlInvalid", {
          defaultValue: "Course URL must start with http:// or https://",
        })
      );
      return;
    }
    setSaving(true);
    try {
      await saveLmsLink(form);
      setForm(emptyForm());
      setMessage(
        t("app.appraisal.lms.saved", {
          defaultValue: "Course saved.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save course");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    setMessage("");
    setError("");
    setForm({
      id: row.id || "",
      name: row.name || "",
      description: row.description || "",
      buttonText: row.buttonText || "Open course",
      buttonLink: row.buttonLink || "",
      isNewTab: row.isNewTab !== false,
      sortOrder: row.sortOrder || 0,
      isActive: row.isActive !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openDeleteConfirm = (row) => {
    setDeleteConfirm({
      id: row.id,
      name: row.name,
      title: t("app.appraisal.lms.deleteTitle", {
        defaultValue: "Deactivate course",
      }),
      body: t("app.appraisal.lms.deleteBody", {
        defaultValue:
          "Remove “{{name}}” from the catalogue? Staff will no longer see it on My PDP.",
        name: row.name,
      }),
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    setError("");
    try {
      await deleteLmsLink(deleteConfirm.id);
      setDeleteConfirm(null);
      if (form.id === deleteConfirm.id) setForm(emptyForm());
      setMessage(
        t("app.appraisal.lms.deactivated", {
          defaultValue: "Course deactivated.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to deactivate course");
      setDeleteConfirm(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="appraisal-page appraisal-lms-admin-page">
      <CommonPageHeader
        icon={<i className="bi bi-mortarboard" />}
        title={t("app.appraisal.lmsTitle", { defaultValue: "Learning courses" })}
        description={t("app.appraisal.lmsSubtitle", {
          defaultValue:
            "Manage the course catalogue staff see on My PDP when LMS is enabled. Links open your external training portal.",
        })}
        rightContent={
          <AppraisalGuideButton
            onClick={() => setHelpKey("lms")}
            label={t("app.appraisal.lms.guideBtn", {
              defaultValue: "How learning courses work",
            })}
          />
        }
      />

      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!loading && (
        <div className="appraisal-framework-layout">
          <section className="appraisal-settings-panel appraisal-framework-panel appraisal-framework-panel--wide">
            <div className="appraisal-settings-panel__head">
              <AppraisalSectionHeader
                icon="bi-mortarboard"
                title={
                  isEditing
                    ? t("app.appraisal.lms.editTitle", { defaultValue: "Edit course" })
                    : t("app.appraisal.lms.addTitle", { defaultValue: "Add course" })
                }
                helpKey="lms"
                onOpenHelp={setHelpKey}
                tone="lms"
              />
              <p className="appraisal-framework-hint">
                {t("app.appraisal.lms.hint", {
                  defaultValue:
                    "Add courses with a clear name, short description, and URL to your training portal. Staff open them from My PDP.",
                })}
              </p>
            </div>

            <form onSubmit={onSave} className="appraisal-framework-form">
              <div className="appraisal-lms-form-grid">
                <label className="appraisal-field">
                  {t("app.appraisal.lms.name", { defaultValue: "Course name" })}
                  <input
                    required
                    disabled={saving}
                    maxLength={APPRAISAL_LIMITS.name}
                    placeholder={t("app.appraisal.lms.namePlaceholder", {
                      defaultValue: "e.g. System design fundamentals",
                    })}
                    value={form.name}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: clampLen(e.target.value, APPRAISAL_LIMITS.name) }))
                    }
                  />
                  <AppraisalCharCount value={form.name} max={APPRAISAL_LIMITS.name} />
                </label>

                <label className="appraisal-field appraisal-lms-form-grid__full">
                  {t("app.appraisal.lms.description", { defaultValue: "Description" })}
                  <textarea
                    rows={3}
                    disabled={saving}
                    maxLength={APPRAISAL_LIMITS.longText}
                    placeholder={t("app.appraisal.lms.descPlaceholder", {
                      defaultValue: "What the course covers and why staff might take it…",
                    })}
                    value={form.description}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        description: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                      }))
                    }
                  />
                  <AppraisalCharCount value={form.description} max={APPRAISAL_LIMITS.longText} />
                </label>

                <label className="appraisal-field">
                  {t("app.appraisal.lms.buttonLabel", { defaultValue: "Button label" })}
                  <input
                    required
                    disabled={saving}
                    maxLength={APPRAISAL_LIMITS.buttonText}
                    placeholder={t("app.appraisal.lms.buttonPlaceholder", {
                      defaultValue: "Open course",
                    })}
                    value={form.buttonText}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        buttonText: clampLen(e.target.value, APPRAISAL_LIMITS.buttonText),
                      }))
                    }
                  />
                  <AppraisalCharCount value={form.buttonText} max={APPRAISAL_LIMITS.buttonText} />
                </label>

                <label className="appraisal-field">
                  {t("app.appraisal.lms.url", { defaultValue: "Course URL" })}
                  <input
                    required
                    type="url"
                    disabled={saving}
                    maxLength={APPRAISAL_LIMITS.buttonLink}
                    placeholder="https://"
                    value={form.buttonLink}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        buttonLink: clampLen(e.target.value, APPRAISAL_LIMITS.buttonLink),
                      }))
                    }
                  />
                  <AppraisalCharCount value={form.buttonLink} max={APPRAISAL_LIMITS.buttonLink} />
                </label>

                <label className="appraisal-field">
                  {t("app.appraisal.lms.sortOrder", { defaultValue: "Sort order" })}
                  <input
                    type="number"
                    disabled={saving}
                    value={form.sortOrder}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, sortOrder: Number(e.target.value) || 0 }))
                    }
                  />
                </label>
              </div>

              <div className="appraisal-lms-checks" role="group" aria-label="Course options">
                <label className={`appraisal-check-option${form.isNewTab ? " is-on" : ""}`}>
                  <input
                    type="checkbox"
                    disabled={saving}
                    checked={!!form.isNewTab}
                    onChange={() => setForm((p) => ({ ...p, isNewTab: !p.isNewTab }))}
                  />
                  <span className="appraisal-check-option__box" aria-hidden>
                    <i className="bi bi-check-lg" />
                  </span>
                  <span className="appraisal-check-option__text">
                    <strong>
                      {t("app.appraisal.lms.openNewTab", {
                        defaultValue: "Open in new tab",
                      })}
                    </strong>
                    <small>
                      {t("app.appraisal.lms.openNewTabHint", {
                        defaultValue: "Course link opens in a separate browser tab",
                      })}
                    </small>
                  </span>
                </label>

                <label className={`appraisal-check-option${form.isActive ? " is-on" : ""}`}>
                  <input
                    type="checkbox"
                    disabled={saving}
                    checked={!!form.isActive}
                    onChange={() => setForm((p) => ({ ...p, isActive: !p.isActive }))}
                  />
                  <span className="appraisal-check-option__box" aria-hidden>
                    <i className="bi bi-check-lg" />
                  </span>
                  <span className="appraisal-check-option__text">
                    <strong>
                      {t("app.appraisal.lms.active", { defaultValue: "Active" })}
                    </strong>
                    <small>
                      {t("app.appraisal.lms.activeHint", {
                        defaultValue: "Visible to staff on My PDP when LMS is enabled",
                      })}
                    </small>
                  </span>
                </label>
              </div>

              <div className="appraisal-framework-form__actions">
                {isEditing ? (
                  <button
                    type="button"
                    className="appraisal-btn"
                    disabled={saving}
                    onClick={() => setForm(emptyForm())}
                  >
                    {t("app.common.cancel", { defaultValue: "Cancel" })}
                  </button>
                ) : null}
                <button type="submit" className="appraisal-btn primary" disabled={saving}>
                  {saving
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : isEditing
                      ? t("app.appraisal.lms.updateCourse", {
                          defaultValue: "Update course",
                        })
                      : t("app.appraisal.lms.addCourse", {
                          defaultValue: "Add course",
                        })}
                </button>
              </div>
            </form>

            <div className="appraisal-framework-list-block">
              <div className="appraisal-framework-list-block__head">
                <span>
                  {t("app.appraisal.lms.listTitle", {
                    defaultValue: "Course catalogue",
                  })}
                </span>
                <span className="appraisal-framework-count">{rows.length}</span>
              </div>

              {rows.length === 0 ? (
                <div className="appraisal-framework-empty">
                  <i className="bi bi-mortarboard" aria-hidden />
                  <p>
                    {t("app.appraisal.lms.empty", {
                      defaultValue:
                        "No courses yet. Add your first learning course above.",
                    })}
                  </p>
                </div>
              ) : (
                <ul className="appraisal-framework-items">
                  {rows.map((r) => {
                    const active = r.isActive !== false;
                    return (
                      <li
                        key={r.id}
                        className={`appraisal-framework-item appraisal-lms-item${!active ? " is-inactive" : ""}`}
                      >
                        <div className="appraisal-framework-item__body">
                          <div className="appraisal-framework-item__meta">
                            <span className="appraisal-lms-pill">
                              #{r.sortOrder ?? 0}
                            </span>
                            {r.isNewTab !== false ? (
                              <span className="appraisal-lms-pill appraisal-lms-pill--soft">
                                <i className="bi bi-box-arrow-up-right" aria-hidden />
                                {t("app.appraisal.lms.newTabShort", {
                                  defaultValue: "New tab",
                                })}
                              </span>
                            ) : null}
                            <span
                              className={`appraisal-lms-status${active ? " is-active" : " is-inactive"}`}
                            >
                              {active
                                ? t("app.appraisal.lms.active", { defaultValue: "Active" })
                                : t("app.appraisal.lms.inactive", {
                                    defaultValue: "Inactive",
                                  })}
                            </span>
                          </div>
                          <strong>{r.name}</strong>
                          {r.description ? <span>{r.description}</span> : null}
                          <span className="appraisal-lms-item__link">
                            <i className="bi bi-link-45deg" aria-hidden />
                            {r.buttonText || "Open course"}
                            {r.buttonLink ? (
                              <a
                                href={r.buttonLink}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {r.buttonLink}
                              </a>
                            ) : null}
                          </span>
                        </div>
                        <div className="appraisal-lms-item__actions">
                          <button
                            type="button"
                            className="appraisal-btn"
                            onClick={() => onEdit(r)}
                          >
                            <i className="bi bi-pencil" aria-hidden />
                            {t("app.common.edit", { defaultValue: "Edit" })}
                          </button>
                          {active ? (
                            <button
                              type="button"
                              className="appraisal-list-delete"
                              onClick={() => openDeleteConfirm(r)}
                              title={t("app.appraisal.lms.deactivate", {
                                defaultValue: "Deactivate",
                              })}
                            >
                              <i className="bi bi-trash3" aria-hidden />
                              <span>
                                {t("app.appraisal.lms.deactivate", {
                                  defaultValue: "Deactivate",
                                })}
                              </span>
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>
      )}

      {deleteConfirm ? (
        <div
          className="appraisal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="appraisal-lms-delete-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteLoading) setDeleteConfirm(null);
          }}
        >
          <div className="appraisal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appraisal-modal__header">
              <div>
                <p className="appraisal-modal__eyebrow">
                  {t("app.appraisal.lmsTitle", { defaultValue: "Learning courses" })}
                </p>
                <h2 className="appraisal-modal__title" id="appraisal-lms-delete-title">
                  {deleteConfirm.title}
                </h2>
              </div>
              <button
                type="button"
                className="appraisal-btn"
                disabled={deleteLoading}
                onClick={() => setDeleteConfirm(null)}
              >
                {t("app.common.close", { defaultValue: "Close" })}
              </button>
            </div>
            <div className="appraisal-modal__body">
              <div className="appraisal-modal__summary">
                <div className="appraisal-modal__icon" aria-hidden>
                  <i className="bi bi-trash3" />
                </div>
                <p className="appraisal-modal__lead">{deleteConfirm.body}</p>
              </div>
              {deleteConfirm.name ? (
                <div className="appraisal-modal__target">
                  <p className="appraisal-modal__target-label">
                    {t("app.appraisal.lms.deleteTarget", { defaultValue: "Course" })}
                  </p>
                  <p className="appraisal-modal__target-name">{deleteConfirm.name}</p>
                </div>
              ) : null}
              <div className="appraisal-modal__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={deleteLoading}
                  onClick={() => setDeleteConfirm(null)}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  className="appraisal-btn danger"
                  disabled={deleteLoading}
                  onClick={confirmDelete}
                >
                  {deleteLoading
                    ? t("app.common.deleting", { defaultValue: "Deleting…" })
                    : t("app.appraisal.lms.deactivate", { defaultValue: "Deactivate" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalLmsPage;
